import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { isPresent } from "~/tools/is-present";
import { Template } from "~/tools/template";
import { useDelayed } from "~/tools/use-delayed";

import { DashboardWidget, type DashboardWidgetProps } from "../_dashboard-widget";

import { DEBOUNCE_QUERIES_MS } from "./_common";
import { FormState } from "./_form-state";

export interface PreviewProps extends Pick<DashboardWidgetProps, "timeRange"> {
	form: FormState;
	className?: string;
}

export const Preview: React.FC<PreviewProps> = ({ form, timeRange, ...props }) => {
	const queries = useComputed(
		(monitor) => {
			// extract the output per element so it can filter out invalid queries
			return form.fields.kind.branches.metrics.fields.queries
				.getElements(monitor)
				.map((element) => element.getOutput(monitor))
				.filter(isPresent);
		},
		[form],
	);

	// debounce the queries before passing them to the widget where it fetches the data
	const debouncedQueries = useDelayed(queries, DEBOUNCE_QUERIES_MS);

	const widget = useComputed(
		(monitor) => {
			const output = form.getOutput(monitor, (_, verbose) => verbose);

			return FormState.toSpec({
				...output,
				name: output.name ?? Template("\u00A0"), // use the spaceholder for name if not set

				// extract the output per element so it can filter out invalid links
				links: form.fields.links
					.getElements(monitor)
					.map((element) => element.getOutput(monitor))
					.filter(isPresent),

				kind: {
					...output.kind,
					branches: {
						...output.kind.branches,
						metrics: {
							...output.kind.branches.metrics,
							queries: debouncedQueries,
						},
					},
				},
			});
		},
		[form, debouncedQueries],
	);

	return <DashboardWidget identity="preview" spec={widget} timeRange={timeRange} {...props} />;
};
